import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from './common/authFetch';
import { useAuth } from './common/AuthContext';
import ConfirmationDialog from './ConfirmationDialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend } from 'recharts';
import './css/Admin.css';
import BackgroundManager from './components/BackgroundManager';
import AnnouncementManager from './components/AnnouncementManager';
import { trackTabView } from './hooks/usePageTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PIE_COLORS = ['#2196f3', '#ff9800', '#4caf50', '#e91e63', '#9c27b0', '#00bcd4', '#ff5722', '#607d8b', '#795548', '#cddc39'];

function Admin() {
    const { t } = useTranslation();
    const { user, hasAttribute } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [userTabConsented, setUserTabConsented] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, confirmLabel: null });

    // ── Dashboard ──
    const [dashStats, setDashStats] = useState(null);
    const [charts, setCharts] = useState(null);
    const [mappingRunning, setMappingRunning] = useState(false);
    const [topMappings, setTopMappings] = useState([]);

    const fetchDashboard = useCallback(async () => {
        try {
            const [statsRes, chartsRes, mappingsRes] = await Promise.all([
                authFetch(`${API_URL}/api/v1/admin/dashboard`),
                authFetch(`${API_URL}/api/v1/admin/dashboard/charts`),
                authFetch(`${API_URL}/api/v1/admin/subject-mapping/top`)
            ]);
            const statsData = await statsRes.json();
            const chartsData = await chartsRes.json();
            const mappingsData = await mappingsRes.json();
            if (statsData.success) setDashStats(statsData);
            if (chartsData.success) setCharts(chartsData);
            if (mappingsData.success) setTopMappings(mappingsData.mappings || []);
        } catch (e) { console.error(e); }
    }, []);

    // ── User List ──
    const [users, setUsers] = useState([]);
    const [userPage, setUserPage] = useState(1);
    const [userTotalPages, setUserTotalPages] = useState(1);
    const [userTotalCount, setUserTotalCount] = useState(0);
    const [userSearch, setUserSearch] = useState('');
    const [userSearchInput, setUserSearchInput] = useState('');
    const [userLoading, setUserLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetail, setUserDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const fetchUsers = useCallback(async (page = 1, search = '') => {
        setUserLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/users/paginated?page=${page}&limit=20&search=${encodeURIComponent(search)}`);
            const data = await res.json();
            if (data.success) {
                setUsers(data.users);
                setUserPage(data.page);
                setUserTotalPages(data.totalPages);
                setUserTotalCount(data.totalCount);
            }
        } catch (e) { console.error(e); }
        finally { setUserLoading(false); }
    }, []);

    const fetchUserDetail = async (userId) => {
        setDetailLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/users/${userId}/detail`);
            const data = await res.json();
            if (data.success) setUserDetail(data);
        } catch (e) { console.error(e); }
        finally { setDetailLoading(false); }
    };

    // ── Trends ──
    const [trends, setTrends] = useState(null);
    const [featureUsage, setFeatureUsage] = useState([]);
    const [pageViews, setPageViews] = useState(null);

    const fetchTrends = useCallback(async () => {
        try {
            const [trendsRes, featureRes, pvRes] = await Promise.all([
                authFetch(`${API_URL}/api/v1/admin/dashboard/trends`),
                authFetch(`${API_URL}/api/v1/admin/dashboard/feature-usage`),
                authFetch(`${API_URL}/api/v1/admin/dashboard/page-views?days=30`)
            ]);
            const trendsData = await trendsRes.json();
            const featureData = await featureRes.json();
            const pvData = await pvRes.json();
            if (trendsData.success) setTrends(trendsData);
            if (featureData.success) setFeatureUsage(featureData.features);
            if (pvData.success) setPageViews(pvData);
        } catch (e) { console.error(e); }
    }, []);

    // ── AI API Usage ──
    const [geminiData, setGeminiData] = useState(null);
    const [geminiLoading, setGeminiLoading] = useState(false);
    const [aiDays, setAiDays] = useState(30);
    const [aiLogPage, setAiLogPage] = useState(1);
    const AI_LOG_PER_PAGE = 30;

    const fetchGeminiUsage = useCallback(async (days = 30) => {
        setGeminiLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/gemini-usage?days=${days}`);
            const data = await res.json();
            if (data.success) { setGeminiData(data); setAiLogPage(1); }
        } catch (e) { console.error(e); }
        finally { setGeminiLoading(false); }
    }, []);

    // ── Access Logs ──
    const [accessLogs, setAccessLogs] = useState([]);
    const [accessLogDate, setAccessLogDate] = useState(() => {
        const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    });
    const [accessLogPage, setAccessLogPage] = useState(1);
    const [accessLogTotalPages, setAccessLogTotalPages] = useState(1);
    const [accessLogTotal, setAccessLogTotal] = useState(0);
    const [accessLogLoading, setAccessLogLoading] = useState(false);
    const [accessLogUserFilter, setAccessLogUserFilter] = useState('');
    const ACCESS_LOG_PER_PAGE = 50;

    const fetchAccessLogs = useCallback(async (date, page = 1, username = '') => {
        setAccessLogLoading(true);
        try {
            const params = new URLSearchParams({ date, page, limit: ACCESS_LOG_PER_PAGE, username });
            const res = await authFetch(`${API_URL}/api/v1/admin/access-logs?${params}`);
            const data = await res.json();
            if (data.success) {
                setAccessLogs(data.logs);
                setAccessLogPage(data.page);
                setAccessLogTotalPages(data.totalPages);
                setAccessLogTotal(data.total);
            }
        } catch (e) { console.error(e); }
        finally { setAccessLogLoading(false); }
    }, []);

    // ── ABAC ──
    const [abacUsers, setAbacUsers] = useState([]);
    const [abacAttributes, setAbacAttributes] = useState([]);
    const [abacLoading, setAbacLoading] = useState(false);

    const fetchAbac = useCallback(async () => {
        setAbacLoading(true);
        try {
            const [usersRes, attrsRes] = await Promise.all([
                authFetch(`${API_URL}/api/v1/admin/abac/users`),
                authFetch(`${API_URL}/api/v1/admin/abac/attributes`)
            ]);
            const usersData = await usersRes.json();
            const attrsData = await attrsRes.json();
            if (usersData.success) setAbacUsers(usersData.users);
            if (attrsData.success) setAbacAttributes(attrsData.attributes);
        } catch (e) { console.error(e); }
        finally { setAbacLoading(false); }
    }, []);

    const handleAbacGrant = async (userId, attributeKey) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/abac/grant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, attributeKey })
            });
            const data = await res.json();
            if (data.success) fetchAbac();
        } catch (e) { console.error(e); }
    };

    const handleAbacRevoke = async (userId, attributeKey) => {
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: t('admin.abac.revokeConfirm', { attribute: attributeKey }),
            confirmLabel: t('admin.abac.revoke'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await authFetch(`${API_URL}/api/v1/admin/abac/revoke`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId, attributeKey })
                    });
                    const data = await res.json();
                    if (data.success) fetchAbac();
                } catch (e) { console.error(e); }
            }
        });
    };

    // ── Admin Settings ──
    const [allAdminUsers, setAllAdminUsers] = useState([]);
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [adminSearchResults, setAdminSearchResults] = useState([]);
    const [adminSearched, setAdminSearched] = useState(false);
    const [adminLoading, setAdminLoading] = useState(false);

    const fetchAdminUsers = useCallback(async () => {
        setAdminLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/users/with-admin`);
            const data = await res.json();
            if (data.success) setAllAdminUsers(data.users);
        } catch (e) { console.error(e); }
        finally { setAdminLoading(false); }
    }, []);

    // ── Tab load ──
    useEffect(() => {
        trackTabView('admin', activeTab);
        if (activeTab === 'dashboard') fetchDashboard();
        else if (activeTab === 'trends') fetchTrends();
        else if (activeTab === 'users') fetchUsers(1, userSearch);
        else if (activeTab === 'geminiUsage') fetchGeminiUsage(aiDays);
        else if (activeTab === 'accessLogs') fetchAccessLogs(accessLogDate, 1, '');
        else if (activeTab === 'adminSettings') { fetchAdminUsers(); if (hasAttribute('abac_mgmt')) fetchAbac(); }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ──
    const handleUserSearch = () => {
        setUserSearch(userSearchInput.trim());
        fetchUsers(1, userSearchInput.trim());
    };

    const handleUserClick = (u) => {
        setSelectedUser(u);
        setUserDetail(null);
        fetchUserDetail(u.user_id);
    };

    const handleDeactivate = (userId) => {
        const u = users.find(x => x.user_id === userId);
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: u?.is_disabled ? t('admin.userList.activateConfirm') : t('admin.userList.deactivateConfirm'),
            confirmLabel: u?.is_disabled ? t('admin.userList.activate') : t('admin.userList.deactivate'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await authFetch(`${API_URL}/api/v1/admin/users/${userId}/deactivate`, { method: 'PUT' });
                    const data = await res.json();
                    if (data.success) {
                        fetchUsers(userPage, userSearch);
                        if (userDetail?.user_id === userId) setUserDetail(prev => prev ? { ...prev, is_disabled: data.is_disabled } : prev);
                    }
                } catch (e) { console.error(e); }
            }
        });
    };

    const handleDeleteUser = (userId) => {
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: t('admin.userList.deleteConfirm'),
            confirmLabel: t('delete'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await authFetch(`${API_URL}/api/v1/admin/users/${userId}`, { method: 'DELETE' });
                    const data = await res.json();
                    if (data.success) {
                        setSelectedUser(null);
                        setUserDetail(null);
                        fetchUsers(userPage, userSearch);
                    }
                } catch (e) { console.error(e); }
            }
        });
    };

    const adminUsers = allAdminUsers.filter(u => u.isAdmin && u.user_id !== user?.user_id);

    const handleAdminSearch = () => {
        const query = adminSearchQuery.trim().toLowerCase();
        if (!query) { setAdminSearchResults([]); setAdminSearched(false); return; }
        setAdminSearchResults(allAdminUsers.filter(u => !u.isAdmin && u.username.toLowerCase().includes(query)));
        setAdminSearched(true);
    };

    const handleToggleAdmin = (targetUserId, username, currentIsAdmin) => {
        if (targetUserId === user?.user_id) return;
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: currentIsAdmin ? t('admin.users.revokeConfirm', { username }) : t('admin.users.grantConfirm', { username }),
            confirmLabel: t('confirm'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await authFetch(`${API_URL}/api/v1/admin/users/${targetUserId}/toggle-admin`, { method: 'PUT' });
                    const data = await res.json();
                    if (data.success) {
                        setAllAdminUsers(prev => prev.map(u => u.user_id === targetUserId ? { ...u, isAdmin: data.isAdmin } : u));
                        if (data.isAdmin) setAdminSearchResults(prev => prev.filter(u => u.user_id !== targetUserId));
                    }
                } catch (e) { console.error(e); }
            }
        });
    };

    const handleRunMapping = async () => {
        setMappingRunning(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/subject-mapping/run`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                // 매핑 완료 후 차트 + 매핑 목록 새로고침
                const [chartsRes, mappingsRes] = await Promise.all([
                    authFetch(`${API_URL}/api/v1/admin/dashboard/charts`),
                    authFetch(`${API_URL}/api/v1/admin/subject-mapping/top`)
                ]);
                const chartsData = await chartsRes.json();
                const mappingsData = await mappingsRes.json();
                if (chartsData.success) setCharts(chartsData);
                if (mappingsData.success) setTopMappings(mappingsData.mappings || []);
            }
        } catch (e) { console.error(e); }
        finally { setMappingRunning(false); }
    };

    const formatSeconds = (sec) => {
        if (!sec) return '0h 0m';
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        return `${h}h ${m}m`;
    };

    const formatDate = (d) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString();
    };

    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        const dt = new Date(d);
        return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    return (
        <div className="admin-container">
            {/* Tabs */}
            <div className="admin-tabs">
                {[
                    { id: 'dashboard' },
                    { id: 'trends' },
                    { id: 'users', attr: 'user_mgmt' },
                    { id: 'accessLogs', attr: 'access_logs' },
                    { id: 'announcements' },
                    { id: 'geminiUsage' },
                    { id: 'adminSettings', attr: 'admin_settings' },
                    { id: 'backgrounds' },
                ].filter(tab => !tab.attr || hasAttribute(tab.attr)).map(tab => (
                    <button
                        key={tab.id}
                        className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => {
                            if (tab.id === 'users' && !userTabConsented) {
                                setConfirmDialog({
                                    isOpen: true,
                                    title: t('admin.userList.accessWarningTitle'),
                                    message: t('admin.userList.accessWarningMessage'),
                                    confirmLabel: t('admin.userList.accessWarningConfirm'),
                                    onConfirm: () => {
                                        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                                        setUserTabConsented(true);
                                        setActiveTab('users');
                                    }
                                });
                                return;
                            }
                            setActiveTab(tab.id);
                        }}
                    >
                        {t(`admin.tabs.${tab.id}`)}
                    </button>
                ))}
            </div>

            {/* ── Dashboard Tab ── */}
            {activeTab === 'dashboard' && (
                <div className="admin-section">
                    <h2>{t('admin.tabs.dashboard')}</h2>
                    {dashStats ? (
                        <div className="dashboard-cards">
                            <div className="dashboard-card">
                                <div className="dashboard-card-icon">👥</div>
                                <div className="dashboard-card-value">{dashStats.totalUsers}</div>
                                <div className="dashboard-card-label">{t('admin.dashboard.totalUsers')}</div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-card-icon">🟢</div>
                                <div className="dashboard-card-value">{dashStats.activeUsers}</div>
                                <div className="dashboard-card-label">{t('admin.dashboard.activeUsers')}</div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-card-icon">📖</div>
                                <div className="dashboard-card-value">{formatSeconds(dashStats.todayStudySeconds)}</div>
                                <div className="dashboard-card-label">{t('admin.dashboard.todayStudy')}</div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-card-icon">📊</div>
                                <div className="dashboard-card-value">{formatSeconds(dashStats.weekStudySeconds)}</div>
                                <div className="dashboard-card-label">{t('admin.dashboard.weekStudy')}</div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-card-icon">📅</div>
                                <div className="dashboard-card-value">{dashStats.totalSchedules}</div>
                                <div className="dashboard-card-label">{t('admin.dashboard.totalSchedules')}</div>
                            </div>
                        </div>
                    ) : (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    )}

                    {/* Charts */}
                    {charts && (
                        <div className="dashboard-charts">
                            {/* 일별 공부 시간 (14일) */}
                            <div className="dashboard-chart-card">
                                <h3>{t('admin.dashboard.dailyStudy')}</h3>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={charts.dailyStudy}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={11} stroke="var(--text-secondary)" />
                                        <YAxis tickFormatter={v => `${Math.round(v / 3600)}h`} fontSize={11} stroke="var(--text-secondary)" />
                                        <Tooltip
                                            formatter={v => formatSeconds(v)}
                                            labelFormatter={l => l}
                                            contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                        />
                                        <Bar dataKey="value" fill="#2196f3" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* 시간대별 공부 분포 */}
                            <div className="dashboard-chart-card">
                                <h3>{t('admin.dashboard.hourlyDist')}</h3>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={charts.hourlyDistribution}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis dataKey="hour" tickFormatter={h => `${h}h`} fontSize={11} stroke="var(--text-secondary)" />
                                        <YAxis tickFormatter={v => `${Math.round(v / 3600)}h`} fontSize={11} stroke="var(--text-secondary)" />
                                        <Tooltip
                                            formatter={v => formatSeconds(v)}
                                            labelFormatter={l => `${l}:00`}
                                            contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                        />
                                        <Bar dataKey="seconds" fill="#ff9800" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* 과목별 공부 시간 TOP 10 */}
                            {charts.topSubjects.length > 0 && (
                                <div className="dashboard-chart-card">
                                    <h3>{t('admin.dashboard.topSubjects')}</h3>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie
                                                data={charts.topSubjects}
                                                dataKey="seconds"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={90}
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                fontSize={11}
                                            >
                                                {charts.topSubjects.map((_, i) => (
                                                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={v => formatSeconds(v)}
                                                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                        </div>
                    )}

                    {/* AI 과목 분류 */}
                    <div className="mapping-section">
                        <div className="mapping-section-header">
                            <h3>{t('admin.dashboard.subjectMapping')}</h3>
                            <button
                                className="mapping-run-btn"
                                onClick={handleRunMapping}
                                disabled={mappingRunning}
                                title={t('admin.dashboard.runMapping')}
                            >
                                {mappingRunning ? '...' : t('admin.dashboard.aiNormalize')}
                            </button>
                        </div>
                        {topMappings.length > 0 ? (
                            <div className="mapping-table-wrapper">
                                <table className="admin-table mapping-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('admin.dashboard.normalizedName')}</th>
                                            <th>{t('admin.dashboard.mappingCount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topMappings.map((m, i) => (
                                            <tr key={m.name}>
                                                <td>{i + 1}</td>
                                                <td><strong>{m.name}</strong></td>
                                                <td>{m.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="admin-no-results">{t('admin.dashboard.noMappings')}</p>
                        )}
                    </div>
                </div>
            )}

            {/* ── Trends Tab ── */}
            {activeTab === 'trends' && (
                <div className="admin-section">
                    <h2>{t('admin.tabs.trends')}</h2>
                    {trends ? (
                        <div className="dashboard-charts">
                            {/* DAU (30일) */}
                            <div className="dashboard-chart-card">
                                <h3>{t('admin.trends.dau')}</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={trends.dau}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={11} stroke="var(--text-secondary)" />
                                        <YAxis fontSize={11} stroke="var(--text-secondary)" allowDecimals={false} />
                                        <Tooltip
                                            labelFormatter={l => l}
                                            contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                        />
                                        <Line type="monotone" dataKey="value" stroke="#2196f3" strokeWidth={2} dot={false} name={t('admin.trends.activeUsers')} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            {/* MAU (12개월) */}
                            <div className="dashboard-chart-card">
                                <h3>{t('admin.trends.mau')}</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={trends.mau}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis dataKey="month" tickFormatter={m => m.slice(5)} fontSize={11} stroke="var(--text-secondary)" />
                                        <YAxis fontSize={11} stroke="var(--text-secondary)" allowDecimals={false} />
                                        <Tooltip
                                            labelFormatter={l => l}
                                            contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                        />
                                        <Bar dataKey="value" fill="#9c27b0" radius={[4, 4, 0, 0]} name={t('admin.trends.activeUsers')} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* DAU + 신규 가입 (30일, 복합) */}
                            <div className="dashboard-chart-card chart-card-wide">
                                <h3>{t('admin.trends.dauVsSignups')}</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={trends.dau.map((d, i) => ({ ...d, signups: trends.dailySignups[i]?.value || 0 }))}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={11} stroke="var(--text-secondary)" />
                                        <YAxis fontSize={11} stroke="var(--text-secondary)" allowDecimals={false} />
                                        <Tooltip
                                            labelFormatter={l => l}
                                            contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                        />
                                        <Legend />
                                        <Line type="monotone" dataKey="value" stroke="#2196f3" strokeWidth={2} dot={false} name={t('admin.trends.dau')} />
                                        <Line type="monotone" dataKey="signups" stroke="#4caf50" strokeWidth={2} dot={false} name={t('admin.trends.newSignups')} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            {/* 메뉴별 사용 비율 + 페이지별 방문 수 — 컴팩트 테이블 */}
                            {(featureUsage.length > 0 || (pageViews && pageViews.totals?.length > 0)) && (
                                <>
                                {featureUsage.length > 0 && (() => {
                                    const maxUsers = Math.max(...featureUsage.map(f => f.users), 1);
                                    return (
                                        <div className="dashboard-chart-card">
                                            <h3>{t('admin.trends.featureUsage')}</h3>
                                            <table className="trends-compact-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('admin.trends.featureName')}</th>
                                                        <th className="trends-bar-col"></th>
                                                        <th className="trends-num-col">{t('admin.trends.featureUsers')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {featureUsage.map((f, i) => (
                                                        <tr key={f.name}>
                                                            <td className="trends-label">{t(`admin.trends.feature.${f.name}`)}</td>
                                                            <td className="trends-bar-cell">
                                                                <div className="trends-bar-bg">
                                                                    <div className="trends-bar-fill" style={{ width: `${(f.users / maxUsers) * 100}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                                                </div>
                                                            </td>
                                                            <td className="trends-num">{f.users}{t('admin.trends.featureUnit')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}

                                {pageViews && pageViews.totals?.length > 0 && (() => {
                                    const maxCount = Math.max(...pageViews.totals.map(p => Number(p.count)), 1);
                                    return (
                                        <div className="dashboard-chart-card">
                                            <h3>{t('admin.trends.pageViews')}</h3>
                                            <table className="trends-compact-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('admin.trends.pageName')}</th>
                                                        <th className="trends-bar-col"></th>
                                                        <th className="trends-num-col">{t('admin.trends.views')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pageViews.totals.map((p, i) => {
                                                        const label = p.tab
                                                            ? `${t(`admin.trends.feature.${p.page}`, p.page)} > ${t(`admin.trends.feature.${p.tab}`, p.tab)}`
                                                            : t(`admin.trends.feature.${p.page}`, p.page);
                                                        const count = Number(p.count);
                                                        return (
                                                            <tr key={`${p.page}-${p.tab || ''}`}>
                                                                <td className="trends-label">{label}</td>
                                                                <td className="trends-bar-cell">
                                                                    <div className="trends-bar-bg">
                                                                        <div className="trends-bar-fill" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                                                    </div>
                                                                </td>
                                                                <td className="trends-num">{count}{t('admin.trends.viewsUnit')}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}
                                </>
                            )}
                        </div>
                    ) : (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    )}
                </div>
            )}

            {/* ── Users Tab ── */}
            {activeTab === 'users' && (
                <div className="admin-section">
                    <h2>{t('admin.userList.title')} ({userTotalCount})</h2>
                    <div className="admin-search-bar">
                        <input
                            type="text"
                            className="admin-search-input"
                            placeholder={t('admin.userList.searchPlaceholder')}
                            value={userSearchInput}
                            onChange={(e) => setUserSearchInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
                        />
                        <button className="admin-search-btn" onClick={handleUserSearch}>{t('admin.users.searchBtn')}</button>
                    </div>

                    {userLoading ? (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    ) : (
                        <>
                            <div className="admin-table-wrapper">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>{t('admin.userList.username')}</th>
                                            <th>{t('admin.userList.email')}</th>
                                            <th>{t('admin.userList.createdAt')}</th>
                                            <th>{t('admin.userList.lastLogin')}</th>
                                            <th>{t('admin.userList.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(u => (
                                            <tr
                                                key={u.user_id}
                                                className={`admin-table-row ${selectedUser?.user_id === u.user_id ? 'selected' : ''} ${u.is_disabled ? 'disabled' : ''}`}
                                                onClick={() => handleUserClick(u)}
                                            >
                                                <td>{u.username}</td>
                                                <td>{u.email}</td>
                                                <td>{formatDate(u.created_at)}</td>
                                                <td>{formatDate(u.last_login_at)}</td>
                                                <td>
                                                    <span className={`status-badge ${u.is_disabled ? 'inactive' : 'active'}`}>
                                                        {u.is_disabled ? t('admin.userList.inactive') : t('admin.userList.active')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {users.length === 0 && (
                                            <tr><td colSpan="5" className="admin-no-results">{t('admin.users.noResults')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {userTotalPages > 1 && (
                                <div className="admin-pagination">
                                    <button disabled={userPage <= 1} onClick={() => fetchUsers(userPage - 1, userSearch)}>◀</button>
                                    <span>{userPage} / {userTotalPages}</span>
                                    <button disabled={userPage >= userTotalPages} onClick={() => fetchUsers(userPage + 1, userSearch)}>▶</button>
                                </div>
                            )}
                        </>
                    )}

                    {/* User Detail Panel */}
                    {selectedUser && (
                        <div className="user-detail-panel">
                            <div className="user-detail-header">
                                <h3>{selectedUser.username}</h3>
                                <button className="user-detail-close" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>&times;</button>
                            </div>
                            {detailLoading ? (
                                <p className="admin-loading">{t('admin.users.loading')}</p>
                            ) : userDetail ? (
                                <>
                                    <div className="user-detail-stats">
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.email')}</span>
                                            <span>{userDetail.email}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.createdAt')}</span>
                                            <span>{formatDate(userDetail.created_at)}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.lastLogin')}</span>
                                            <span>{formatDate(userDetail.last_login_at)}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.subjects')}</span>
                                            <span>{userDetail.subjectCount}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.schedules')}</span>
                                            <span>{userDetail.scheduleCount}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.studyTime')}</span>
                                            <span>{formatSeconds(userDetail.totalStudySeconds)}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.streak')}</span>
                                            <span>{userDetail.streak ? `${userDetail.streak.current} (${t('admin.userList.longest')}: ${userDetail.streak.longest})` : 'N/A'}</span>
                                        </div>
                                        <div className="user-detail-stat">
                                            <span className="user-detail-stat-label">{t('admin.userList.badges')}</span>
                                            <span>{userDetail.badgeCount}</span>
                                        </div>
                                    </div>

                                    {/* Recent Activity */}
                                    <h4 className="user-detail-section-title">{t('admin.userList.recentActivity')}</h4>
                                    {userDetail.recentSessions.length === 0 ? (
                                        <p className="admin-no-results">{t('admin.userList.noActivity')}</p>
                                    ) : (
                                        <div className="user-detail-activity">
                                            {userDetail.recentSessions.map(s => (
                                                <div key={s.session_id} className="activity-item">
                                                    <span className="activity-subject">{s.subject || '-'}</span>
                                                    <span className="activity-time">{formatSeconds(s.seconds)}</span>
                                                    <span className="activity-date">{formatDateTime(s.start_time)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="user-detail-actions">
                                        <button
                                            className={`admin-toggle-btn ${userDetail.is_disabled ? 'grant' : 'revoke'}`}
                                            onClick={() => handleDeactivate(userDetail.user_id)}
                                        >
                                            {userDetail.is_disabled ? t('admin.userList.activate') : t('admin.userList.deactivate')}
                                        </button>
                                        <button
                                            className="admin-toggle-btn revoke"
                                            onClick={() => handleDeleteUser(userDetail.user_id)}
                                        >
                                            {t('delete')}
                                        </button>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}
                </div>
            )}

            {/* ── Access Logs Tab ── */}
            {activeTab === 'accessLogs' && (
                <div className="admin-section">
                    <h2>{t('admin.tabs.accessLogs')}</h2>
                    <div className="access-log-filters">
                        <div className="access-log-filter-row">
                            <input
                                type="date"
                                className="admin-search-input"
                                value={accessLogDate}
                                onChange={(e) => setAccessLogDate(e.target.value)}
                                style={{ maxWidth: 180 }}
                            />
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder={t('admin.accessLogs.filterUser')}
                                value={accessLogUserFilter}
                                onChange={(e) => setAccessLogUserFilter(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && fetchAccessLogs(accessLogDate, 1, accessLogUserFilter)}
                                style={{ maxWidth: 180 }}
                            />
                            <button
                                className="admin-search-btn"
                                onClick={() => fetchAccessLogs(accessLogDate, 1, accessLogUserFilter)}
                            >
                                {t('admin.accessLogs.search')}
                            </button>
                        </div>
                        <div className="access-log-summary">
                            {t('admin.accessLogs.totalCount', { count: accessLogTotal })}
                        </div>
                    </div>

                    {accessLogLoading ? (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    ) : accessLogs.length > 0 ? (
                        <>
                            <div className="admin-table-wrapper">
                                <table className="admin-table access-log-table">
                                    <thead>
                                        <tr>
                                            <th>{t('admin.accessLogs.time')}</th>
                                            <th>{t('admin.accessLogs.user')}</th>
                                            <th>{t('admin.accessLogs.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accessLogs.map((log, i) => (
                                            <tr key={`${log.timestamp}-${i}`}>
                                                <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                    {formatDateTime(log.timestamp)}
                                                </td>
                                                <td>{log.username || '-'}</td>
                                                <td>
                                                    <span className={`status-badge ${log.success ? 'active' : 'inactive'}`}>
                                                        {log.success ? 'OK' : 'FAIL'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {accessLogTotalPages > 1 && (
                                <div className="admin-pagination">
                                    <button disabled={accessLogPage <= 1} onClick={() => { setAccessLogPage(p => p - 1); fetchAccessLogs(accessLogDate, accessLogPage - 1, accessLogUserFilter); }}>◀</button>
                                    <span>{accessLogPage} / {accessLogTotalPages}</span>
                                    <button disabled={accessLogPage >= accessLogTotalPages} onClick={() => { setAccessLogPage(p => p + 1); fetchAccessLogs(accessLogDate, accessLogPage + 1, accessLogUserFilter); }}>▶</button>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="admin-no-results">{t('admin.accessLogs.noData')}</p>
                    )}
                </div>
            )}

            {/* ── Admin Settings Tab ── */}
            {activeTab === 'adminSettings' && (
                <div className="admin-section">
                    <h2>{t('admin.users.title')}</h2>

                    {/* 관리자 목록 + 권한 통합 */}
                    <h3 className="admin-sub-title">{t('admin.users.adminList')}</h3>
                    {adminLoading ? (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>{t('admin.abac.username')}</th>
                                        <th>{t('admin.abac.attributes')}</th>
                                        <th>{t('admin.abac.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminUsers.map(u => {
                                        const userAttrs = abacUsers.find(au => au.user_id === u.user_id)?.attributes || [];
                                        return (
                                            <tr key={u.user_id}>
                                                <td>
                                                    <span className="user-item-name">{u.username}</span>
                                                    <span className="admin-badge" style={{ marginLeft: 6 }}>Admin</span>
                                                </td>
                                                <td>
                                                    {hasAttribute('abac_mgmt') ? (
                                                        <div className="abac-attr-list">
                                                            {abacAttributes.map(a => {
                                                                const has = userAttrs.includes(a.key);
                                                                return (
                                                                    <button
                                                                        key={a.key}
                                                                        className={`abac-attr-toggle ${has ? 'active' : ''}`}
                                                                        onClick={() => has ? handleAbacRevoke(u.user_id, a.key) : handleAbacGrant(u.user_id, a.key)}
                                                                        title={t(`admin.abac.attrLabels.${a.key}`, a.key)}
                                                                    >
                                                                        {t(`admin.abac.attrLabels.${a.key}`, a.key)}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="abac-attr-list">
                                                            {userAttrs.map(a => (
                                                                <span key={a} className="abac-attr-badge">
                                                                    {t(`admin.abac.attrLabels.${a}`, a)}
                                                                </span>
                                                            ))}
                                                            {userAttrs.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>-</span>}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <button className="admin-toggle-btn revoke" onClick={() => handleToggleAdmin(u.user_id, u.username, true)}>
                                                        {t('admin.users.revoke')}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {adminUsers.length === 0 && !adminLoading && (
                                        <tr><td colSpan={3}><p className="admin-no-results">{t('admin.users.noAdmins')}</p></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <h3 className="admin-sub-title admin-sub-title-search">{t('admin.users.addAdmin')}</h3>
                    <div className="admin-search-bar">
                        <input
                            type="text"
                            className="admin-search-input"
                            placeholder={t('admin.users.search')}
                            value={adminSearchQuery}
                            onChange={(e) => setAdminSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAdminSearch()}
                        />
                        <button className="admin-search-btn" onClick={handleAdminSearch}>{t('admin.users.searchBtn')}</button>
                    </div>
                    {adminSearched && (
                        <div className="user-list">
                            {adminSearchResults.map(u => (
                                <div key={u.user_id} className="user-item">
                                    <div className="user-item-info">
                                        <span className="user-item-name">{u.username}</span>
                                    </div>
                                    <button className="admin-toggle-btn grant" onClick={() => handleToggleAdmin(u.user_id, u.username, false)}>
                                        {t('admin.users.grant')}
                                    </button>
                                </div>
                            ))}
                            {adminSearchResults.length === 0 && (
                                <p className="admin-no-results">{t('admin.users.noResults')}</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── AI API Usage Tab ── */}
            {activeTab === 'geminiUsage' && (
                <div className="admin-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h2 style={{ margin: 0 }}>{t('admin.tabs.geminiUsage')}</h2>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {[7, 14, 30, 60, 90].map(d => (
                                <button
                                    key={d}
                                    className={`mapping-run-btn${aiDays === d ? ' active' : ''}`}
                                    style={aiDays === d ? { background: '#2196f3', color: '#fff', borderColor: '#2196f3' } : {}}
                                    onClick={() => { setAiDays(d); fetchGeminiUsage(d); }}
                                >
                                    {d}{t('admin.gemini.daysUnit')}
                                </button>
                            ))}
                        </div>
                    </div>
                    {geminiLoading ? (
                        <p className="admin-loading">{t('admin.users.loading')}</p>
                    ) : geminiData ? (
                        <>
                            {/* 총 통계 카드 */}
                            <div className="dashboard-cards">
                                <div className="dashboard-card">
                                    <div className="dashboard-card-icon">📞</div>
                                    <div className="dashboard-card-value">{Number(geminiData.totalStats.total_calls).toLocaleString()}</div>
                                    <div className="dashboard-card-label">{t('admin.gemini.totalCalls')}</div>
                                </div>
                                <div className="dashboard-card">
                                    <div className="dashboard-card-icon">🔤</div>
                                    <div className="dashboard-card-value">{Number(geminiData.totalStats.total_tokens).toLocaleString()}</div>
                                    <div className="dashboard-card-label">{t('admin.gemini.totalTokens')}</div>
                                </div>
                                <div className="dashboard-card">
                                    <div className="dashboard-card-icon">📥</div>
                                    <div className="dashboard-card-value">{Number(geminiData.totalStats.total_input).toLocaleString()}</div>
                                    <div className="dashboard-card-label">{t('admin.gemini.inputTokens')}</div>
                                </div>
                                <div className="dashboard-card">
                                    <div className="dashboard-card-icon">📤</div>
                                    <div className="dashboard-card-value">{Number(geminiData.totalStats.total_output).toLocaleString()}</div>
                                    <div className="dashboard-card-label">{t('admin.gemini.outputTokens')}</div>
                                </div>
                                <div className="dashboard-card">
                                    <div className="dashboard-card-icon">❌</div>
                                    <div className="dashboard-card-value">{Number(geminiData.totalStats.total_errors)}</div>
                                    <div className="dashboard-card-label">{t('admin.gemini.errors')}</div>
                                </div>
                            </div>

                            {/* 기능별 사용량 요약 (모델은 부가 정보) */}
                            {geminiData.summary.length > 0 && (() => {
                                const maxTokens = Math.max(...geminiData.summary.map(s => Number(s.total_tokens)), 1);
                                return (
                                    <div className="mapping-section">
                                        <div className="mapping-section-header">
                                            <h3>{t('admin.gemini.summaryByFeature')}</h3>
                                        </div>
                                        <table className="trends-compact-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('admin.gemini.feature')}</th>
                                                    <th className="trends-bar-col"></th>
                                                    <th className="trends-num-col">{t('admin.gemini.calls')}</th>
                                                    <th className="trends-num-col">{t('admin.gemini.tokens')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {geminiData.summary.map((s, i) => (
                                                    <tr key={`${s.feature}-${s.model}`}>
                                                        <td className="trends-label">
                                                            {t(`admin.gemini.featureLabel.${s.feature}`, s.feature)}
                                                            <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginTop: 1 }}>{s.model}</span>
                                                        </td>
                                                        <td className="trends-bar-cell">
                                                            <div className="trends-bar-bg">
                                                                <div className="trends-bar-fill" style={{ width: `${(Number(s.total_tokens) / maxTokens) * 100}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                                            </div>
                                                        </td>
                                                        <td className="trends-num">{Number(s.call_count)}</td>
                                                        <td className="trends-num">{Number(s.total_tokens).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}

                            {/* 일별 사용량 차트 */}
                            {geminiData.daily.length > 0 && (
                                <div className="mapping-section">
                                    <div className="mapping-section-header">
                                        <h3>{t('admin.gemini.dailyUsage')}</h3>
                                    </div>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={geminiData.daily}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                            <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={11} stroke="var(--text-secondary)" />
                                            <YAxis fontSize={11} stroke="var(--text-secondary)" />
                                            <Tooltip
                                                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                                formatter={(v, name) => [Number(v).toLocaleString(), name === 'total_tokens' ? t('admin.gemini.tokens') : t('admin.gemini.calls')]}
                                                labelFormatter={l => l}
                                            />
                                            <Area type="monotone" dataKey="total_tokens" stroke="#2196f3" fill="rgba(33,150,243,0.15)" name={t('admin.gemini.tokens')} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* 최근 호출 로그 (페이징) */}
                            {geminiData.recentLogs.length > 0 && (() => {
                                const totalLogPages = Math.ceil(geminiData.recentLogs.length / AI_LOG_PER_PAGE);
                                const pagedLogs = geminiData.recentLogs.slice((aiLogPage - 1) * AI_LOG_PER_PAGE, aiLogPage * AI_LOG_PER_PAGE);
                                return (
                                    <div className="mapping-section">
                                        <div className="mapping-section-header">
                                            <h3>{t('admin.gemini.recentLogs')} ({geminiData.recentLogs.length})</h3>
                                        </div>
                                        <div className="admin-table-wrapper">
                                            <table className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('admin.gemini.feature')}</th>
                                                        <th>{t('admin.gemini.tokens')}</th>
                                                        <th>{t('admin.userList.status')}</th>
                                                        <th>{t('admin.gemini.time')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pagedLogs.map(log => (
                                                        <tr key={log.id}>
                                                            <td>
                                                                {t(`admin.gemini.featureLabel.${log.feature}`, log.feature)}
                                                                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)' }}>{log.model}</span>
                                                            </td>
                                                            <td>{Number(log.total_tokens).toLocaleString()}</td>
                                                            <td>
                                                                <span className={`status-badge ${log.success ? 'active' : 'inactive'}`}>
                                                                    {log.success ? 'OK' : 'ERR'}
                                                                </span>
                                                            </td>
                                                            <td style={{ fontSize: '12px' }}>{formatDateTime(log.called_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {totalLogPages > 1 && (
                                            <div className="admin-pagination">
                                                <button disabled={aiLogPage <= 1} onClick={() => setAiLogPage(p => p - 1)}>◀</button>
                                                <span>{aiLogPage} / {totalLogPages}</span>
                                                <button disabled={aiLogPage >= totalLogPages} onClick={() => setAiLogPage(p => p + 1)}>▶</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <p className="admin-no-results">{t('admin.gemini.noData')}</p>
                    )}
                </div>
            )}

            {/* ── Announcements Tab ── */}
            {activeTab === 'announcements' && <AnnouncementManager />}

            {/* ── Backgrounds Tab ── */}
            {activeTab === 'backgrounds' && <BackgroundManager />}

            <ConfirmationDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                confirmLabel={confirmDialog.confirmLabel || t('confirm')}
                isDanger={confirmDialog.confirmLabel === t('delete')}
            />
        </div>
    );
}

export default Admin;
